import React, { useState } from 'react';
import { t } from 'i18next';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import TabMenu from '../util/TabMenu';
import TabPanel from '@mui/lab/TabPanel';
import Tasks from '../timesheetsComp/Tasks';
import CategoryManagementNew from '../settingsComponents/CategoryManagementNew';

interface RecountChange {
  weekLabel: string;
  oldHours: number;
  newHours: number;
}
interface RecountDiff {
  userId: number;
  firstName: string;
  lastName: string;
  changes: RecountChange[];
}
interface RecountResult {
  totalUsers: number;
  usersFixed: number;
  diffs: RecountDiff[];
}

export const ToilRecountPanel: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RecountResult | null>(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post<RecountResult>('toils/recount-company');
      setResult(data);
      if (data.usersFixed > 0) {
        enqueueSnackbar(`Fixed ${data.usersFixed} user(s)`, { variant: 'warning' });
      } else {
        enqueueSnackbar('Recalculation complete — no discrepancies found', { variant: 'success' });
      }
    } catch {
      enqueueSnackbar('Error running recalculation', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <div style={{ fontSize: 13, color: 'var(--text-color-calendar-week)', marginBottom: 16, maxWidth: 520, lineHeight: 1.6 }}>
        Recalculates TOIL totals for all active employees by comparing stored records against
        actual submitted time reports. Any discrepancies are listed below.
      </div>

      <button
        type="button"
        disabled={loading}
        onClick={run}
        className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[6px] px-[18px] rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)]"
        style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, opacity: loading ? .6 : 1 }}
      >
        <span style={{ fontSize: 15 }}>⟳</span>
        {loading ? 'Recalculating…' : 'Recalculate all TOIL for company'}
      </button>

      {result && (
        <div style={{ marginTop: 24 }}>
          {/* Summary bar */}
          <div style={{
            display: 'flex', gap: 16, padding: '12px 16px', borderRadius: 8,
            background: result.usersFixed > 0 ? 'rgba(217,119,6,.08)' : 'rgba(5,150,105,.08)',
            border: `1px solid ${result.usersFixed > 0 ? 'rgba(217,119,6,.25)' : 'rgba(5,150,105,.25)'}`,
            marginBottom: 16,
          }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 2 }}>Total users</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--text-color)' }}>{result.totalUsers}</div>
            </div>
            <div style={{ width: 1, background: 'var(--border-input)' }} />
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', marginBottom: 2 }}>Discrepancies fixed</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: result.usersFixed > 0 ? '#d97706' : '#059669' }}>{result.usersFixed}</div>
            </div>
            {result.usersFixed === 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8, fontSize: 13, fontWeight: 600, color: '#059669' }}>
                ✓ All TOIL records are up to date
              </div>
            )}
          </div>

          {/* Diff table */}
          {result.diffs.length > 0 && (
            <div style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-input)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-top)' }}>
                    <th style={{ padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)' }}>Employee</th>
                    <th style={{ padding: '8px 14px', textAlign: 'center', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)' }}>Week</th>
                    <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)' }}>Was</th>
                    <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)' }}>Now</th>
                    <th style={{ padding: '8px 14px', textAlign: 'right', fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', borderBottom: '1px solid var(--border-input)' }}>Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {result.diffs.flatMap(diff =>
                    diff.changes.map((ch, i) => {
                      const delta = ch.newHours - ch.oldHours;
                      return (
                        <tr key={`${diff.userId}-${ch.weekLabel}-${i}`} style={{ borderBottom: '1px solid var(--border-input)', background: 'var(--btn-user-form)' }}>
                          <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>
                            {i === 0 ? `${diff.firstName} ${diff.lastName}` : ''}
                          </td>
                          <td style={{ padding: '9px 14px', textAlign: 'center', fontSize: 12, color: 'var(--text-color-calendar-week)', fontWeight: 600 }}>{ch.weekLabel}</td>
                          <td style={{ padding: '9px 14px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: '#ef4444', fontWeight: 700 }}>{ch.oldHours}h</td>
                          <td style={{ padding: '9px 14px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: '#059669', fontWeight: 700 }}>{ch.newHours}h</td>
                          <td style={{ padding: '9px 14px', textAlign: 'right', fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 800, color: delta > 0 ? '#059669' : '#ef4444' }}>
                            {delta > 0 ? '+' : ''}{Math.round(delta * 100) / 100}h
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TimesheetsSettings = () => {
  const [value, setValue] = useState('category');

  return (
    <div className="text-[var(--text-color)]">
      <TabMenu value={value} onChange={(v: string) => setValue(v)} tabs={[
        { label: t('Category') || 'Category', value: 'category' },
        { label: t('TasksType') || 'Tasks',   value: 'tasks' },
      ]}>
        <TabPanel value="category" sx={{ padding: 0 }}>
          <CategoryManagementNew updateTree={() => {}} />
        </TabPanel>
        <TabPanel value="tasks" sx={{ padding: 0 }}>
          <Tasks />
        </TabPanel>
      </TabMenu>
    </div>
  );
};

export default TimesheetsSettings;
